import { useEffect, useState, useCallback } from 'react';

/**
 * Хук для работы с геолокацией пользователя.
 * Запрашивает разрешение, получает текущую позицию и обновляет каждые 30 секунд.
 *
 * @returns {{ userGeoLocation: string|null, geoPermissionStatus: string, refreshGeo: Function }}
 */
const useGeolocation = () => {
  const [userGeoLocation, setUserGeoLocation] = useState(null);
  const [geoPermissionStatus, setGeoPermissionStatus] = useState('prompt');

  // Запрос разрешения и первичная позиция
  useEffect(() => {
    const requestGeoPermission = async () => {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          setGeoPermissionStatus(result.state);
          result.onchange = () => {
            setGeoPermissionStatus(result.state);
          };
        } catch (e) {
          console.log('Permissions API не поддерживается');
        }
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setUserGeoLocation(`${pos.coords.latitude},${pos.coords.longitude}`);
            setGeoPermissionStatus('granted');
          },
          (err) => {
            console.warn('Геолокация не получена:', err);
            setGeoPermissionStatus('denied');
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }
    };
    requestGeoPermission();
  }, []);

  // Обновление каждые 30 секунд
  useEffect(() => {
    const geoInterval = setInterval(() => {
      if (navigator.geolocation && geoPermissionStatus === 'granted') {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setUserGeoLocation(`${pos.coords.latitude},${pos.coords.longitude}`);
          },
          (err) => console.warn('Ошибка обновления геолокации:', err),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }
    }, 30000);

    return () => clearInterval(geoInterval);
  }, [geoPermissionStatus]);

  const refreshGeo = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserGeoLocation(`${pos.coords.latitude},${pos.coords.longitude}`);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  }, []);

  return { userGeoLocation, setUserGeoLocation, geoPermissionStatus, refreshGeo };
};

export default useGeolocation;
